import { activePacks, region } from '../region';
import { config } from '../storage/config';
import type { RegionManifest } from '../../types';

// The media pack (audio + images, potentially hundreds of MB) is NOT precached
// by the service worker. Instead the user explicitly downloads it into a
// versioned Cache Storage bucket, from which the app reads cache-first.

function bucketName(m: RegionManifest): string {
  return `syrinx-pack-${m.id}-${m.version}`;
}

/**
 * Delete pack buckets left behind by earlier versions of this region.
 *
 * Bumping `version` renames the bucket, which is how a content change (a
 * re-encode, a re-crop) reaches clients that already hold the old bytes. But
 * nothing was reclaiming the abandoned bucket, so each bump silently doubled
 * up: a user who downloaded the pack at 2026.07 kept all ~8 MB of it forever
 * alongside the new one, and it compounded with every subsequent bump.
 *
 * Safe to call on every boot — it only ever touches buckets for this region id
 * whose version is not the live one.
 */
export async function evictStalePacks(m: RegionManifest): Promise<string[]> {
  const live = bucketName(m);
  const prefix = `syrinx-pack-${m.id}-`;
  const stale = (await caches.keys()).filter((k) => k.startsWith(prefix) && k !== live);
  await Promise.all(stale.map((k) => caches.delete(k)));
  return stale;
}

export function packBaseUrl(m: RegionManifest): string {
  // Most specific wins: a hand-set runtime override (e.g. a local-network host)
  // beats the deployment's config.json, which beats the manifest's baked-in
  // value. In the PocketBase deployment config.json points at the same origin,
  // so the pack is served by the same container as the app.
  const override = localStorage.getItem('syrinx.packBaseUrl');
  const configured = configuredPackBaseUrl();
  return (override || configured || m.packBaseUrl || '').replace(/\/$/, '');
}

/** config.json's packBaseUrl, or '' if config hasn't loaded (it always has by boot). */
function configuredPackBaseUrl(): string {
  try {
    return config().packBaseUrl;
  } catch {
    return '';
  }
}

/**
 * Every media path referenced by the manifest, mapped to its size in bytes.
 *
 * A Map rather than a list because both callers want the sizes: the download
 * estimate sums them, and the resume estimate sums only the missing ones.
 */
export function packAssetSizes(m: RegionManifest): Map<string, number> {
  const sizes = new Map<string, number>();
  const add = (path: string, bytes: number) => {
    if (path && !sizes.has(path)) sizes.set(path, bytes);
  };
  for (const sp of m.species) {
    for (const a of sp.audio) {
      add(a.file, a.bytes); // a.peaks is inline in the manifest, not a pack asset
    }
    for (const p of sp.photos) {
      add(p.file, p.bytes);
      // thumbBytes is absent in manifests built before the pipeline recorded it;
      // those under-report the pack by the size of its thumbnails (~8%).
      add(p.thumb, p.thumbBytes ?? 0);
    }
  }
  return sizes;
}

/** All media paths referenced by the region manifest. */
export function packAssetPaths(m: RegionManifest): string[] {
  return [...packAssetSizes(m).keys()];
}

export interface PackSize {
  bytes: number;
  files: number;
}

/**
 * What downloading the whole pack will cost, straight from the manifest — so it
 * can be shown *before* the first byte is fetched, which is the entire point.
 */
export function packSize(m: RegionManifest): PackSize {
  const sizes = packAssetSizes(m);
  let bytes = 0;
  for (const n of sizes.values()) bytes += n;
  return { bytes, files: sizes.size };
}

export interface StorageInfo {
  usage: number;
  quota: number;
  persisted: boolean;
}

export async function storageInfo(): Promise<StorageInfo> {
  const est = (await navigator.storage?.estimate?.()) ?? {};
  const persisted = (await navigator.storage?.persisted?.()) ?? false;
  return { usage: est.usage ?? 0, quota: est.quota ?? 0, persisted };
}

/**
 * Is this response actually a pack asset, rather than a static host's
 * catch-all page?
 *
 * PocketBase — and most SPA-friendly static hosts — answer a request for a
 * missing file with `index.html` and a **200**, so `res.ok` is not enough.
 * Caching one of those would be permanent damage, not a transient miss: reads
 * are cache-first, so a bucket poisoned while the pack was still missing would
 * keep serving HTML as audio long after the real pack was uploaded. Nothing in
 * a pack is HTML, which makes this a reliable tell.
 */
function isPackAsset(res: Response): boolean {
  return !(res.headers.get('content-type') ?? '').startsWith('text/html');
}

/**
 * Pack assets always bypass the browser's HTTP cache: this Cache Storage bucket
 * is their real cache, so the HTTP cache would only store a second copy of the
 * whole pack — and worse, it holds onto the catch-all page. A device that
 * opened the app before the pack was uploaded caches those HTML 200s under the
 * asset URLs and keeps serving them long after the pack exists, which is
 * invisible from the server side.
 */
const PACK_FETCH: RequestInit = { cache: 'no-store' };

/**
 * The absolute form of a pack URL, which is what Cache Storage keys entries by:
 * `cache.keys()` hands back fully-resolved request URLs, and the pack base can
 * be a relative path when the pack ships from the app's own origin.
 */
function absUrl(url: string): string {
  try {
    return new URL(url, globalThis.location?.href).href;
  } catch {
    return url;
  }
}

/**
 * In development, ask the server before trusting the bucket.
 *
 * Deployed, cache-first is the whole design: the bytes behind a pack URL never
 * change without a `version` bump, which renames the bucket. On a laptop they
 * change constantly — the curation endpoint re-encodes a clip under its
 * existing filename every time you re-cut it — and cache-first meant the local
 * app went on playing the clip you just replaced until you bumped `version`,
 * i.e. until you did a deployment-shaped thing to see a local edit. The cached
 * copy is still written and still used when the fetch fails, so downloading the
 * pack and pulling the plug behaves the same in both.
 */
// Read defensively: stage 06 imports packAssetPaths from this module to decide
// what the pruner may delete, and under tsx there is no `import.meta.env` at
// all — a bare `import.meta.env.DEV` here takes the whole pipeline down at load.
const FRESH_FIRST = Boolean((import.meta as { env?: { DEV?: boolean } }).env?.DEV);

/**
 * Which active pack an asset belongs to, read from its own path.
 *
 * Pack-relative paths are region-prefixed by construction — "capetown/audio/
 * xc123.opus" — and each pack has its own Cache Storage bucket, so with more
 * than one selected, taking the bucket from the primary manifest would look a
 * Cape Town clip up in the Pacific Northwest's bucket and miss every time. It
 * would then re-fetch on every play and never cache, which offline reads as the
 * pack simply not being there.
 *
 * Falls back to the primary manifest for a path with no recognised prefix,
 * which is what the single-pack case and every existing test look like.
 */
function manifestForPath(path: string): RegionManifest {
  const id = path.split('/')[0];
  return activePacks().find((m) => m.id === id) ?? region();
}

/** Resolve a pack-relative media path to a Response, cache-first. */
export async function fetchMedia(path: string): Promise<Response | null> {
  const m = manifestForPath(path);
  const cache = await caches.open(bucketName(m));
  const url = `${packBaseUrl(m)}/${path}`;
  const hit = await cache.match(url);
  let cached: Response | null = null;
  if (hit) {
    if (isPackAsset(hit)) cached = hit;
    else await cache.delete(url); // heal a bucket poisoned before this check existed
  }
  if (cached && !FRESH_FIRST) return cached;
  try {
    const res = await fetch(url, PACK_FETCH);
    if (res.ok && isPackAsset(res)) {
      await cache.put(url, res.clone());
      return res;
    }
  } catch {
    // Offline — fall back to the bucket, or to the caller's own fallback
    // (e.g. synthetic audio) when there isn't one.
  }
  return cached;
}

export interface DownloadProgress {
  /** Assets fetched so far *this run*, not counting what was already here. */
  done: number;
  /** Assets this run set out to fetch — the missing ones, not the whole pack. */
  total: number;
  bytes: number;
  /** Assets that couldn't be fetched — usually "the pack isn't uploaded yet". */
  failed: number;
}

/**
 * Download every pack asset that isn't already here, reporting progress.
 *
 * Idempotent: it resumes after an abort, and resuming means *resuming* — the
 * missing set is worked out once, up front, by the same reckoning `packStatus`
 * uses for the button's own "Download 4.2 MB remaining". It used to walk all
 * 1199 paths instead, opening a cached Response apiece to re-decide what it
 * already knew, so a resume counted "1/1199 … 2/1199 …" past a thousand files
 * it wasn't fetching. That reads as a fresh download of the whole pack, takes
 * long enough on a phone to look like one, and is the wrong number besides:
 * what a learner wants counted here is the part they are waiting for.
 */
export async function downloadPack(
  m: RegionManifest,
  onProgress: (p: DownloadProgress) => void,
  signal?: AbortSignal,
): Promise<void> {
  await navigator.storage?.persist?.();
  const cache = await caches.open(bucketName(m));
  const base = packBaseUrl(m);
  const sizes = packAssetSizes(m);
  const here = await cachedPaths(cache, base, sizes);
  const paths = [...sizes.keys()].filter((p) => !here.has(p));

  let done = 0;
  let bytes = 0;
  const total = paths.length;
  const CONCURRENCY = 6;
  // Nothing to do — say so once rather than leaving the caller on "Downloading…".
  if (!total) return onProgress({ done: 0, total: 0, bytes: 0, failed: 0 });

  /** Fetch one asset into the bucket. False means it isn't there afterwards. */
  async function fetchInto(path: string): Promise<boolean> {
    const url = `${base}/${path}`;
    try {
      const res = await fetch(url, { ...PACK_FETCH, signal });
      if (res.ok && isPackAsset(res)) {
        // content-length is missing on some hosts; the manifest knows the size
        // anyway, so the byte counter never flatlines.
        bytes += Number(res.headers.get('content-length')) || sizes.get(path) || 0;
        await cache.put(url, res.clone());
        return true;
      }
      // A 404, or the host's catch-all page standing in for one. Delete
      // unconditionally: this path is one `cachedPaths` ruled out, which
      // includes an entry poisoned with that catch-all page, and deleting a URL
      // the bucket doesn't hold is a no-op.
      await cache.delete(url);
      return false;
    } catch (err) {
      if ((err as Error).name === 'AbortError') throw err;
      // Skip a single failed asset; the pack remains partially usable.
      return false;
    }
  }

  let cursor = 0;
  const missed: string[] = [];
  async function worker(): Promise<void> {
    while (cursor < paths.length) {
      if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
      const path = paths[cursor++];
      if (!(await fetchInto(path))) missed.push(path);
      done++;
      onProgress({ done, total, bytes, failed: missed.length });
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, total) }, () => worker()),
  );

  // One more try for whatever didn't come, one at a time, before calling it
  // missing. Six parallel fetches over a phone's connection drop the occasional
  // asset for reasons that have nothing to do with the host, and a dropped one
  // is invisible until weeks later a bird turns up without its photograph — the
  // pack having reported itself complete the whole time. A second request is a
  // cheap price for not having to find that by hand.
  //
  // Not when most of the run failed, though: that is a pack that was never
  // uploaded, or a device that went offline mid-download, and neither is
  // answered by asking a thousand more times.
  if (missed.length <= total / 2) {
    for (const path of missed.splice(0)) {
      if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
      if (!(await fetchInto(path))) missed.push(path);
      onProgress({ done, total, bytes, failed: missed.length });
    }
  }
}

export interface PackStatus {
  /** Assets present in the cache bucket. */
  have: number;
  total: number;
  /** 0..1 — what the progress meter draws. */
  fraction: number;
  /** Bytes still to fetch, from the manifest's own accounting. */
  remainingBytes: number;
}

/**
 * Which of the manifest's assets this bucket already holds.
 *
 * The one reckoning of "what is here", shared by the status the You screen
 * reports and the work list the download builds from it. They used to have one
 * each and they did not agree: the status counted the bucket's keys, and the
 * download re-opened every cached Response to decide the same thing again. Two
 * answers to one question is how a button offering "4.2 MB remaining" opened a
 * run that walked the whole pack.
 */
async function cachedPaths(
  cache: Cache,
  base: string,
  sizes: Map<string, number>,
): Promise<Set<string>> {
  const abs = (p: string) => absUrl(`${base}/${p}`);

  // One keys() call for the whole bucket, not one match() per asset. At 120
  // species the pack is ~800 files, and a match() apiece — each of which opens
  // the response body — made the You screen visibly stall on open.
  const keys = new Set((await cache.keys()).map((r) => r.url));
  const present = [...sizes.keys()].filter((p) => keys.has(abs(p)));

  // keys() carries no headers, so it can't tell a real asset from a catch-all
  // page cached under its URL (see isPackAsset). No current code path can put
  // HTML in a bucket, but a bucket written before that guard existed can be
  // full of it — so spot-check a few entries, and only pay for the exact
  // per-asset walk when one of them looks poisoned.
  if (!present.length || !(await looksPoisoned(cache, abs, present))) {
    return new Set(present);
  }
  const verified = new Set<string>();
  for (const path of present) {
    const hit = await cache.match(abs(path));
    if (hit && isPackAsset(hit)) verified.add(path);
  }
  return verified;
}

/** How much of the pack is already on this device. */
export async function packStatus(m: RegionManifest): Promise<PackStatus> {
  const sizes = packAssetSizes(m);
  const total = sizes.size;
  if (!total) return { have: 0, total: 0, fraction: 0, remainingBytes: 0 };

  const cache = await caches.open(bucketName(m));
  const here = await cachedPaths(cache, packBaseUrl(m), sizes);
  let remainingBytes = 0;
  for (const [path, bytes] of sizes) {
    if (!here.has(path)) remainingBytes += bytes;
  }

  return { have: here.size, total, fraction: here.size / total, remainingBytes };
}

/** How many cached entries the integrity spot-check reads. */
const POISON_SAMPLE = 5;

async function looksPoisoned(
  cache: Cache,
  abs: (path: string) => string,
  present: string[],
): Promise<boolean> {
  // Spread the sample across the bucket rather than taking the first few: a
  // pack that went missing part-way through a download is poisoned only in its
  // tail.
  const step = Math.max(1, Math.floor(present.length / POISON_SAMPLE));
  const sample: string[] = [];
  for (let i = 0; i < present.length && sample.length < POISON_SAMPLE; i += step) {
    sample.push(present[i]);
  }
  const hits = await Promise.all(sample.map((p) => cache.match(abs(p))));
  return hits.some((h) => h && !isPackAsset(h));
}
